/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, RefreshControl, ScrollView, Text, View } from 'react-native';
import { useNavigation, type NavigationProp, type ParamListBase } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { MobilePagination } from '../../shared/ui/MobilePagination';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketDeskFooter } from '../../shared/ticket/TicketDeskFooter'
import { ticketColors } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { FilterSheetButton } from '../../shared/filters/FilterSheetButton'
import { FilterSheetModal } from '../../shared/filters/FilterSheetModal';
import { ExpenseSettleModal } from '../../expenses/components/ExpenseSettleModal';
import { usePurchasesScreen } from './usePurchasesScreen';
import { PurchasesScreenHeader } from '../components/PurchasesScreenHeader';
import { PurchasesFilterSheetContent, type PurchasesFilterDraft } from '../components/PurchasesFilterSheetContent';
import { PurchaseOrderRowCard } from '../components/PurchaseOrderRowCard';
import { PurchaseOrderRegisterModal } from '../components/PurchaseOrderRegisterModal';
import { PurchaseOrderDetailModal } from '../components/PurchaseOrderDetailModal';
import { SupplierAccountModal } from '../components/SupplierAccountModal';
import { PurchaseReturnModal } from '../components/PurchaseReturnModal';
import { PurchaseExpenseFiscalModal } from '../components/PurchaseExpenseFiscalModal';
import { StockTransferModal } from '../components/StockTransferModal';
import { openCatalogMasters } from '../../catalog/app-api';
import { PURCHASES_LIST_PAGE_SIZE } from '../domain/purchaseTypes';
import { purchaseOrderMissingFiscal, purchaseRowPaymentPill } from '../domain/purchaseQueues';
import { todayIsoDate } from '../../expenses/infra/expenseListSql';
import { purchasesScreenStyles as styles } from './purchasesScreen.styles';

export function PurchasesScreen() {
  const vm = usePurchasesScreen();
  const { layoutWide } = useTabWorkspaceLayout();
  const branches = useSessionStore((state) => state.branches);
  const navigation = useNavigation<NavigationProp<ParamListBase>>();

  useScreenLoad('Purchases', async (opts) => {
    await vm.loadFn(opts);
  });

  // ── Filter sheet state ──
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<PurchasesFilterDraft | null>(null);

  const openFilterSheet = useCallback(() => {
    setFilterDraft({
      statusFilter: vm.statusFilter,
      originFilter: vm.originFilter,
      supplierFilter: vm.supplierFilter,
      sortBy: vm.sortBy,
      sortDir: vm.sortDir,
    });
    setFilterSheetOpen(true);
  }, [vm.statusFilter, vm.originFilter, vm.supplierFilter, vm.sortBy, vm.sortDir]);

  const applyFilterDraft = useCallback(() => {
    if (!filterDraft) return;
    if (filterDraft.statusFilter !== vm.statusFilter) vm.setStatusFilter(filterDraft.statusFilter);
    if (filterDraft.originFilter !== vm.originFilter) vm.setOriginFilter(filterDraft.originFilter);
    if (filterDraft.supplierFilter !== vm.supplierFilter) vm.setSupplierFilter(filterDraft.supplierFilter);
    if (filterDraft.sortBy !== vm.sortBy) vm.setSortBy(filterDraft.sortBy);
    if (filterDraft.sortDir !== vm.sortDir) vm.setSortDir(filterDraft.sortDir);
    setFilterSheetOpen(false);
  }, [filterDraft, vm]);

  const clearFilterDraft = useCallback(() => {
    setFilterDraft({
      statusFilter: 'all',
      originFilter: 'all',
      supplierFilter: 'all',
      sortBy: 'date',
      sortDir: 'desc',
    });
  }, []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (vm.statusFilter !== 'all') n += 1;
    if (vm.originFilter !== 'all') n += 1;
    if (vm.supplierFilter !== 'all') n += 1;
    if (vm.sortBy !== 'date' || vm.sortDir !== 'desc') n += 1;
    return n;
  }, [vm.statusFilter, vm.originFilter, vm.supplierFilter, vm.sortBy, vm.sortDir]);

  // Pill de pago por orden ('Por pagar' / 'Vencida') según gastos ligados.
  const paymentByOrder = useMemo(() => {
    const today = todayIsoDate();
    const grouped = new Map<string, typeof vm.queueExpenses>();
    for (const expense of vm.queueExpenses) {
      const list = grouped.get(expense.purchaseOrderId) ?? [];
      list.push(expense);
      grouped.set(expense.purchaseOrderId, list);
    }
    const map: Record<string, ReturnType<typeof purchaseRowPaymentPill>> = {};
    for (const [orderId, expenses] of grouped) {
      map[orderId] = purchaseRowPaymentPill(expenses, today);
    }
    return map;
  }, [vm.queueExpenses]);

  const missingFiscalByOrder = useMemo(() => {
    if (!vm.fiscalCaptureEnabled) return {} as Record<string, boolean>;
    const grouped = new Map<string, typeof vm.queueExpenses>();
    for (const expense of vm.queueExpenses) {
      const list = grouped.get(expense.purchaseOrderId) ?? [];
      list.push(expense);
      grouped.set(expense.purchaseOrderId, list);
    }
    const map: Record<string, boolean> = {};
    for (const [orderId, expenses] of grouped) {
      map[orderId] = purchaseOrderMissingFiscal(expenses, true);
    }
    return map;
  }, [vm.queueExpenses, vm.fiscalCaptureEnabled]);

  const filterTrigger = (
    <FilterSheetButton
      activeCount={activeFilterCount}
      onOpen={openFilterSheet}
      variant={layoutWide ? 'desk' : 'ticket'}
      style={layoutWide ? undefined : styles.filterTriggerMobile}
    />
  );

  if (!vm.canView) {
    return (
      <ShellContentFrame>
        <View style={styles.container} testID="purchases-screen">
          <ModuleEmptyState
            title="Compras"
            subtitle="No tenés permiso para ver este módulo."
          />
        </View>
      </ShellContentFrame>
    );
  }

  const showEmptyState = vm.orders.length === 0 && !vm.loading;

  // BUG-5 fix: distinguir "sin órdenes" de "hay órdenes pero los filtros las ocultan".
  const hasActiveSearchOrFilters =
    vm.search.trim() !== '' ||
    activeFilterCount > 0 ||
    vm.queueFilter !== 'all';
  const filteredEmpty = showEmptyState && hasActiveSearchOrFilters;

  // Criterio Ventas: sin órdenes y sin filtros activos, ocultar KPIs en 0 y chips.
  const hideMetrics =
    vm.summary.totalOrders === 0 &&
    activeFilterCount === 0 &&
    vm.queueFilter === 'all' &&
    vm.search.trim() === '';

  const openNewOrder = () => {
    vm.setRegisterMode('order');
    vm.setRegisterOpen(true);
  };
  const openBuyNow = () => {
    vm.setRegisterMode('direct');
    vm.setRegisterOpen(true);
  };

  const header = (
    <PurchasesScreenHeader
      summary={vm.summary}
      search={vm.search}
      onSearchChange={vm.setSearch}
      queueFilter={vm.queueFilter}
      onQueueFilterChange={vm.setQueueFilter}
      onNewOrder={openNewOrder}
      onBuyNow={openBuyNow}
      onOpenSupplierAccount={vm.handleOpenSupplierAccountPicker}
      onTransfer={() => vm.setTransferOpen(true)}
      canEdit={vm.canEdit}
      canTransfer={branches.length > 1}
      filterTrigger={filterTrigger}
      hideMetrics={hideMetrics}
    />
  );

  const emptyList = filteredEmpty ? (
    <ModuleEmptyState
      title="Nada coincide con esos filtros"
      subtitle="Probá cambiar la búsqueda o limpiar los filtros para ver todas las órdenes."
      primaryAction={{
        label: 'Limpiar búsqueda y filtros',
        onPress: () => {
          vm.clearAllFilters();
          setFilterDraft(null);
        },
        testID: 'purchases.empty.clear-filters',
      }}
    />
  ) : (
    <ModuleEmptyState
      loading={vm.loading}
      loadingText="Cargando órdenes…"
      title="No hay órdenes de compra"
      subtitle="Creá tu primera orden de compra para empezar a registrar mercadería de tus proveedores."
      primaryAction={
        vm.canEdit
          ? { label: 'Nueva orden', onPress: openNewOrder, testID: 'purchases.empty.new-order' }
          : undefined
      }
      secondaryAction={
        vm.canEdit
          ? { label: 'Comprar ahora', onPress: openBuyNow }
          : undefined
      }
    />
  );

  // ── readOnlyBanner ──
  const readOnlyBanner = !vm.canEdit ? (
    <View style={posUi.readOnlyBanner}>
      <Text style={posUi.readOnlyText}>Solo lectura. Podés ver las órdenes, no cambiarlas.</Text>
    </View>
  ) : null;

  const purchasesModals = (
    <>
      <PurchaseOrderRegisterModal
        visible={vm.registerOpen}
        mode={vm.registerMode}
        onClose={() => {
          vm.setRegisterOpen(false);
          vm.setRegisterPrefill(null);
        }}
        onSubmit={vm.handleCreateOrder}
        branchId={vm.currentBranchId ?? ''}
        userId={vm.userId}
        submitting={vm.submitting}
        branches={branches}
        initialPrefill={vm.registerPrefill}
        onAddSupplier={() => {
          // Sin proveedores no hay OC posible: cerrar y abrir Catálogo → Proveedores.
          vm.setRegisterOpen(false);
          vm.setRegisterPrefill(null);
          openCatalogMasters(navigation, 'suppliers');
        }}
        fiscalCaptureEnabled={vm.fiscalCaptureEnabled}
        activeCashSession={vm.activeCashSessionInfo}
        expectedCashInDrawer={vm.expectedCashInDrawer}
        onOpenCash={() => navigation.navigate('CashManagement' as never)}
      />
      <PurchaseOrderDetailModal
        visible={!!vm.detailTarget}
        order={vm.detailTarget}
        onClose={vm.handleCloseDetail}
        onVoid={vm.handleVoidOrder}
        onSend={vm.handleSendOrder}
        onReturn={(order) => vm.handleOpenReturn(order)}
        onPay={(expense) => void vm.handleOpenPay(expense)}
        onDuplicate={(order) => vm.handleDuplicateAsDraft(order)}
        onCloseLeftover={vm.handleCloseLeftover}
        onVoidReception={vm.handleVoidReception}
        onVoidReturn={vm.handleVoidReturn}
        linkedExpense={vm.linkedExpense}
        linkedExpenses={vm.linkedExpenses}
        receptions={vm.receptions}
        returns={vm.returns}
        onReceive={({ order, items, generateExpense, alreadyPaid, paymentOnReceive, fiscal }) => {
          vm.handleReceiveOrder({
            receptionId: crypto.randomUUID(),
            branchId: order.branchId,
            purchaseOrderId: order.id,
            supplierId: order.supplierId,
            supplierName: order.supplierName,
            receivedBy: vm.userId,
            receivedAt: new Date().toISOString(),
            items,
            generateExpense,
            alreadyPaid,
            paymentOnReceive,
            fiscal,
          });
        }}
        activeCashSession={vm.activeCashSessionInfo}
        expectedCashInDrawer={vm.expectedCashInDrawer}
        onOpenCash={() => navigation.navigate('CashManagement' as never)}
        fiscalCaptureEnabled={vm.fiscalCaptureEnabled}
        onCompleteExpenseFiscal={vm.handleOpenCompleteExpenseFiscal}
        onRegisterSupplierCreditNote={vm.handleRegisterSupplierCreditNoteFromReturn}
        canEdit={vm.canEdit}
        canReceive={vm.canReceive}
        canVoid={vm.canVoid}
        canReturn={vm.canReturn}
        canPay={vm.canPay}
        hasReturn={vm.hasReturn}
        submitting={vm.submitting}
        createdBy={vm.userId}
      />
      {vm.settleOpen && vm.linkedExpense ? (
        <ExpenseSettleModal
          visible
          expense={vm.linkedExpense}
          amountText={vm.settleAmountText}
          onAmountTextChange={vm.setSettleAmountText}
          activeCashSession={vm.activeCashSessionInfo}
          submitting={vm.submitting}
          onClose={() => vm.setSettleOpen(false)}
          onOpenCash={() => navigation.navigate('CashManagement' as never)}
          canPayWithCheck={vm.canPayWithCheck}
          checkOptions={vm.portfolioChecks}
          checksLoading={vm.portfolioChecksLoading}
          onConfirm={({ method, note, paidDate, checkId }) => {
            void vm.handleSettleExpense({ method, note, paidDate, checkId });
          }}
        />
      ) : null}
      {vm.ConfirmModal}
      <PurchaseExpenseFiscalModal
        visible={vm.fiscalCompleteExpense != null}
        expense={vm.fiscalCompleteExpense}
        branchId={vm.fiscalCompleteExpense?.branchId ?? vm.currentBranchId ?? null}
        expenseAmount={vm.fiscalCompleteExpense?.amount ?? null}
        title="Completar factura"
        subtitle="Cargá el comprobante que te mandó el proveedor. Entra al Libro IVA compras."
        submitting={vm.submitting}
        onClose={() => vm.setFiscalCompleteExpense(null)}
        onSubmit={(fiscal) => void vm.handleSaveCompleteExpenseFiscal(fiscal)}
      />
      <PurchaseExpenseFiscalModal
        visible={vm.supplierCreditNoteModal != null}
        expense={null}
        branchId={vm.supplierCreditNoteModal?.branchId ?? vm.currentBranchId ?? null}
        expenseAmount={vm.supplierCreditNoteModal?.amount ?? null}
        title="Registrar NC del proveedor"
        subtitle="La NC la emite tu proveedor. Completá PV, número y desglose cuando tengas el PDF."
        submitting={vm.submitting}
        initialDraft={vm.supplierCreditNoteModal?.initialDraft ?? null}
        onClose={() => vm.setSupplierCreditNoteModal(null)}
        onSubmit={(fiscal) => void vm.handleSaveSupplierCreditNote(fiscal)}
      />
      <SupplierAccountModal
        visible={!!vm.supplierAccountTarget}
        supplierId={vm.supplierAccountTarget?.id ?? null}
        supplierName={vm.supplierAccountTarget?.name ?? null}
        branchId={vm.currentBranchId ?? ''}
        canPay={vm.canPay}
        onPayBalance={(supplierId) => void vm.handlePaySupplierBalance(supplierId)}
        onClose={() => vm.setSupplierAccountTarget(null)}
      />
      <PurchaseReturnModal
        visible={!!vm.returnTarget}
        order={vm.returnTarget ? {
          id: vm.returnTarget.id,
          branchId: vm.returnTarget.branchId,
          supplierId: vm.returnTarget.supplierId,
          supplierName: vm.returnTarget.supplierName,
        } : null}
        receptions={vm.returnReceptions}
        submitting={vm.submitting}
        onConfirm={(params) => vm.handleReturnOrder({ ...params, returnedBy: vm.userId })}
        onClose={() => {
          vm.setReturnTarget(null);
          vm.setReturnReceptions([]);
        }}
      />
      <StockTransferModal
        visible={vm.transferOpen}
        currentBranchId={vm.currentBranchId ?? ''}
        branches={branches}
        submitting={vm.submitting}
        onConfirm={(params) => vm.handleStockTransfer({ ...params, transferredBy: vm.userId })}
        onClose={() => vm.setTransferOpen(false)}
      />
    </>
  );

  const filterSheet = (
    <FilterSheetModal
      visible={filterSheetOpen}
      onClose={() => setFilterSheetOpen(false)}
      onApply={applyFilterDraft}
      onClear={clearFilterDraft}
      appearance="ticket"
      kicker="Órdenes de compra"
      title="Filtros"
      subtitle="Estado, proveedor, origen y orden."
      hasActiveDraft={
        filterDraft != null &&
        (filterDraft.statusFilter !== 'all' ||
          filterDraft.originFilter !== 'all' ||
          filterDraft.supplierFilter !== 'all' ||
          filterDraft.sortBy !== 'date' ||
          filterDraft.sortDir !== 'desc')
      }
    >
      {filterDraft ? (
        <PurchasesFilterSheetContent
          draft={filterDraft}
          setDraft={(updater) => setFilterDraft((prev) => (prev ? updater(prev) : prev))}
        />
      ) : null}
    </FilterSheetModal>
  );

  if (layoutWide) {
    return (
      <ShellContentFrame style={[styles.container, !layoutWide && styles.containerCompact]}>
        <View style={styles.listFlex} testID="purchases-screen">
          <ScrollView
            style={styles.listFlex}
            contentContainerStyle={styles.wideScrollContent}
            refreshControl={
              <RefreshControl
                refreshing={vm.loading}
                onRefresh={() => vm.loadFn({ force: true })}
                colors={[ticketColors.brandPrimary]}
                tintColor={ticketColors.brandPrimary}
              />
            }
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.stack}>
              {readOnlyBanner}
              {header}
              {showEmptyState ? (
                emptyList
              ) : (
                <PaperSheet style={styles.listSheet} testID="purchases.list.sheet">
                  {vm.orders.map((item) => (
                    <PurchaseOrderRowCard
                      key={item.id}
                      item={item}
                      payment={paymentByOrder[item.id] ?? null}
                      missingFiscal={missingFiscalByOrder[item.id] ?? false}
                      onOpenDetail={vm.handleOpenDetail}
                    />
                  ))}
                  <TicketDeskFooter
                    pageIndex={vm.page}
                    pageSize={PURCHASES_LIST_PAGE_SIZE}
                    productCount={vm.orders.length}
                    totalCount={vm.total}
                    hasMore={vm.page < vm.totalPages - 1}
                    loading={vm.loading}
                    onPreviousPage={() => vm.setPage(Math.max(0, vm.page - 1))}
                    onNextPage={() => vm.setPage(vm.page + 1)}
                    embedded
                  />
                </PaperSheet>
              )}
            </View>
          </ScrollView>
        </View>
        {purchasesModals}
        {filterSheet}
        {vm.detailLoading && !vm.detailTarget ? (
          <View style={styles.detailLoadingOverlay}>
            <ActivityIndicator color={ticketColors.brandPrimary} />
          </View>
        ) : null}
      </ShellContentFrame>
    );
  }

  // Mobile
  return (
    <ShellContentFrame style={[styles.container, !layoutWide && styles.containerCompact]}>
      <View style={styles.listFlex} testID="purchases-screen">
        <FlatList
          style={styles.listFlex}
          data={vm.orders}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <PurchaseOrderRowCard
              item={item}
              payment={paymentByOrder[item.id] ?? null}
              missingFiscal={missingFiscalByOrder[item.id] ?? false}
              onOpenDetail={vm.handleOpenDetail}
            />
          )}
          ListHeaderComponent={
            <View style={styles.stack}>
              {readOnlyBanner}
              {header}
            </View>
          }
          ListEmptyComponent={emptyList}
          ListFooterComponent={
            !showEmptyState ? (
              <MobilePagination
                pageIndex={vm.page}
                pageSize={PURCHASES_LIST_PAGE_SIZE}
                totalCount={vm.total}
                productCount={vm.orders.length}
                loading={vm.loading}
                hasMore={vm.page < vm.totalPages - 1}
                onPreviousPage={() => vm.setPage(Math.max(0, vm.page - 1))}
                onNextPage={() => vm.setPage(vm.page + 1)}
              />
            ) : null
          }
          onRefresh={() => vm.loadFn({ force: true })}
          refreshing={vm.loading}
          keyboardShouldPersistTaps="handled"
        />
      </View>
      {purchasesModals}
      {filterSheet}
      {vm.detailLoading && !vm.detailTarget ? (
        <View style={styles.detailLoadingOverlay}>
          <ActivityIndicator color={ticketColors.brandPrimary} />
        </View>
      ) : null}
    </ShellContentFrame>
  );
}
